import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Menu from "./Menu";
import SortDropdown from "./SortDropdown";
import Checkbox from "components/__reusable/Checkbox";
import StudentInfo from "./StudentInfo";
import useAppSelector from "hooks/useAppSelector";
import useAppDispatch from "hooks/useAppDispatch";
import { getStudents } from "features/student/studentAction";
import {
  resetSelectedStudents,
  setSelectedStudents,
} from "features/student/studentSlice";
import Container from "components/__reusable/Container";
import { Student } from "types/Student";
import { formatDate } from "utilities/DateFormatter";
import { SupportedLang } from "config/constants";
import LoadingDialog from "components/__reusable/LoadingDialog";
import { getTrainingPictures } from "services/trainingPictureServices";

const StudentTable = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { students, sortBy, selectedStudents, loading } = useAppSelector(
    (state) => state.student
  );
  const { facility } = useAppSelector((state) => state.facility);
  const dispatch = useAppDispatch();

  const [trainingPictureCounts, setTrainingPictureCounts] = useState<{
    [key: number]: number;
  }>({});
  const fetchStudents = () =>
    !isNaN(parseInt(id || "")) &&
    dispatch(
      getStudents({
        classId: parseInt(id || ""),
        sortValue: sortBy,
      })
    );
  const callFetchStudents = useCallback(fetchStudents, [
    id,
    dispatch,
    getStudents,
    sortBy,
  ]);
  useEffect(() => {
    callFetchStudents();
  }, [callFetchStudents]);

  const fetchTrainingPictureCount = async (studentId: number) => {
    try {
      const pictures = await getTrainingPictures(studentId.toString());
      return pictures.filter((picture) => picture.is_usable).length;
    } catch (e) {
      return 0;
    }
  };

  useEffect(() => {
    if (students.length > 0) {
      // Fetch training picture counts for all students
      const fetchAllCounts = async () => {
        const counts: { [key: number]: number } = {};
        for (const student of students) {
          counts[student.id] = await fetchTrainingPictureCount(student.id);
        }
        setTrainingPictureCounts(counts);
      };

      fetchAllCounts();
    }
  }, [students, id]);

  const renderDeliveryNg = (student: Student) => {
    if (student.is_ng) {
      return <span style={{ fontWeight: "bold" }}>※</span>;
    }
    return "";
  };

  const renderTrainingPictureCount = (student: Student) => {
    if (student.is_ng) return "";
    const count = trainingPictureCounts[student.id];
    if (count > 0) {
      return `${count}枚`;
    }
    return <span style={{ color: "red", fontWeight: "bold" }}>!</span>;
  };

  const renderParentsRegisterDate = (student: Student) => {
    if (!student.parent) return "";
    else
      return formatDate(new Date(student.parent.create_time), {
        [SupportedLang.ja]: "yo Mo do",
        [SupportedLang.en]: "MMMM, d, yyyy",
      });
  };

  const renderHasParentRegistered = (student: Student) => {
    if (student.is_ng) return "";
    if (student.parent) {
      return <span>○</span>;
    }
    return <span style={{ color: "red", fontWeight: "bold" }}>!</span>;
  };

  const renderHasParentPhotos = (student: Student) => {
    if (student.is_ng) return "";
    if (!student.parent) return "";
    if (student.parent.has_upload_training_picture) {
      return <span>○</span>;
    }
    return <span style={{ color: "red", fontWeight: "bold" }}>!</span>;
  };

  const renderThisMonthPlan = (student: Student) => {
    if (!facility || !student.parent) return "";

    // For free_trial facility plan, always show ファミリー（無料体験中）
    if (facility.active_plan === "free_trial") {
      return <span style={{ color: "#DC747E" }}>ファミリー（無料体験中）</span>;
    }

    // For under_contract facility plan, check parent's accessible_plan
    if (facility.active_plan === "under_contract") {
      switch (student.parent.accessible_plan) {
        case "Free":
          return <span style={{ color: "#E1BC58" }}>フリー</span>;
        case null:
          return <span style={{ color: "#E1BC58" }}>フリー</span>;
        case "Premium":
          return <span style={{ color: "#507ABB" }}>プレミアム</span>;
        case "Family":
          return <span style={{ color: "#DC747E" }}>ファミリー</span>;
        default:
          return "";
      }
    }

    return "";
  };

  const renderNextMonthPlan = (student: Student) => {
    if (!facility || !student.parent) return "";

    // For free_trial next month plan, always show ファミリー（無料体験中）
    if (facility.next_month_plan === "free_trial") {
      return <span style={{ color: "#DC747E" }}>ファミリー（無料体験中）</span>;
    }

    // For under_contract next month plan, check parent's accessible_plan
    if (facility.next_month_plan === "under_contract") {
      if (!student.parent.plan_id)
        return <span style={{ color: "#E1BC58" }}>フリー</span>;
      switch (student.parent.plan_id.name) {
        case "Free":
          return <span style={{ color: "#E1BC58" }}>フリー</span>;
        case "Premium":
          return <span style={{ color: "#507ABB" }}>プレミアム</span>;
        case "Family":
          return <span style={{ color: "#DC747E" }}>ファミリー</span>;
        default:
          return "";
      }
    }

    return "";
  };

  const selectAllStudents = (status: boolean) => {
    dispatch(resetSelectedStudents());
    for (let student of students) {
      dispatch(setSelectedStudents({ isAdd: status, student }));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetSelectedStudents());
    };
  }, []);

  return (
    <div className="flex-1">
      <Container>
        {loading && <LoadingDialog />}
        <div className="h-full flex flex-col space-y-1">
          <div className="lg:flex lg:justify-between lg:items-center space-y-3 lg:space-y-0">
            <div className="flex-1 lg:flex lg:items-center lg:space-x-10 space-y-3 lg:space-y-0">
              <div className="w-fit">
                <p>
                  {t("_student._total_student")}
                  {students.length}
                </p>
                <p>
                  {selectedStudents.length > 0 &&
                    t("_student._selected_students", {
                      total: selectedStudents.length,
                    })}
                </p>
              </div>
              <div className="w-full lg:w-1/2">
                <Menu />
              </div>
            </div>
            <div className="w-full lg:w-1/4">
              <SortDropdown />
            </div>
          </div>
          <div className="flex-1 overflow-auto relative">
            <div className="w-full h-full absolute">
              {!loading && students.length === 0 && (
                <p>{t("_generic._no_data")}</p>
              )}
              {!loading && students.length > 0 && (
                <div className="w-full overflow-x-auto">
                  <table className="min-w-[900px] w-full relative">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="p-2 min-w-64 bg-[#f7f7f7] border border-[#D9D9D9]">
                          <div className="flex justify-between items-center">
                            <Checkbox
                              name={"all"}
                              label={""}
                              isChecked={
                                selectedStudents.length === students.length
                              }
                              onChange={selectAllStudents}
                            />
                            <p className="flex-1 text-center">
                              {t("_student._student_name")}
                            </p>
                          </div>
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px]">
                          {t("_student._delivery")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px]">
                          {t("_student._number_of_face_photos")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px]">
                          {t("_student._is_parent_registered")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px]">
                          {t("_student._has_photos")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[180px]">
                          {t("_student._parents_registration_date")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[140px]">
                          {t("_student._this_month_plan")}
                        </th>
                        <th className="p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[140px]">
                          {t("_student._next_month_plan")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id}>
                          <td className="p-2 border border-[#D9D9D9]">
                            <div className="flex items-center space-x-3">
                              <Checkbox
                                name={student.id.toString()}
                                label={""}
                                isChecked={
                                  selectedStudents.findIndex(
                                    (s) => s.id === student.id
                                  ) >= 0
                                }
                                onChange={(status) =>
                                  dispatch(
                                    setSelectedStudents({
                                      isAdd: status,
                                      student,
                                    })
                                  )
                                }
                              />
                              <StudentInfo student={student} />
                            </div>
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderDeliveryNg(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderTrainingPictureCount(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderHasParentRegistered(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderHasParentPhotos(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderParentsRegisterDate(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderThisMonthPlan(student)}
                          </td>
                          <td className="p-2 border border-[#D9D9D9] text-center">
                            {renderNextMonthPlan(student)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default StudentTable;
